/**
 * CorrespondenceFieldEditor
 *
 * A field editor for correspondence templates that clearly indicates
 * the source of each field's value (override, matter context, or missing).
 */
import { useMemo } from 'react';

import { cn } from '@/lib/utils';

import type { MissingFieldInfo } from '../hooks';
import {
  type FieldValueSource,
  getFieldDisplayName,
  getFieldValueInfo,
  getOverrideKey,
  isFieldRequired,
  isFieldValueMissing,
  type MatterContext,
  type TemplateField,
} from '../utils/fieldValidation';

/** Badge showing field value source */
const FieldSourceBadge: React.FC<{
  source: FieldValueSource;
  isMissing?: boolean;
  inline?: boolean;
}> = ({ source, isMissing, inline }) => {
  const baseClass = inline
    ? 'text-[9px] ml-1.5 px-1 py-0.5 rounded'
    : 'text-xs ml-2 px-1.5 py-0.5 rounded';

  if (isMissing) {
    return (
      <span className={cn(baseClass, 'bg-red-100 text-red-700')}>missing</span>
    );
  }

  if (source === 'empty') return null;

  switch (source) {
    case 'override':
      return (
        <span className={cn(baseClass, 'bg-violet-100 text-violet-700')}>
          override
        </span>
      );
    case 'context':
      return (
        <span className={cn(baseClass, 'bg-blue-100 text-blue-600')}>
          from matter
        </span>
      );
    case 'default':
      return (
        <span className={cn(baseClass, 'bg-gray-100 text-gray-600')}>
          default
        </span>
      );
    default:
      return null;
  }
};

interface CorrespondenceFieldEditorProps {
  /** Template fields from validation response - authoritative source */
  templateFields?: TemplateField[];
  matterContext: MatterContext;
  /** Current overrides keyed by context key (may include pending unsaved changes) */
  overrides: Record<string, string>;
  /** Saved overrides on the component (to distinguish from pending edits) */
  savedOverrides?: Record<string, string>;
  /** Set an override value. Key is context_key/field_reference, not field.id */
  setOverride: (key: string, value: string) => void;
  /** Missing fields from validation - used to show missing indicator */
  missingFields?: Record<string, MissingFieldInfo>;
  inline?: boolean;
}

export const CorrespondenceFieldEditor: React.FC<
  CorrespondenceFieldEditorProps
> = ({
  templateFields,
  matterContext,
  overrides,
  savedOverrides,
  setOverride,
  missingFields,
  inline = false,
}) => {
  const filteredFields = useMemo(() => {
    return (
      templateFields?.filter(
        (field) => field.field_type === 'TEXT' || field.field_type === 'DATE',
      ) ?? []
    );
  }, [templateFields]);

  // Build a set of missing field IDs for quick lookup
  const missingFieldIds = useMemo(() => {
    if (!missingFields) return new Set<string>();
    return new Set(Object.values(missingFields).map((f) => f.fieldId));
  }, [missingFields]);

  const handleInputChange =
    (fieldId: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setOverride(fieldId, e.target.value);
    };

  const containerClassName = inline
    ? ''
    : 'shrink-0 max-h-[70vh] overflow-y-auto border rounded-lg p-6 bg-white shadow';

  if (filteredFields.length === 0) {
    return (
      <div className={containerClassName}>
        <p
          className={inline ? 'text-xs text-gray-500' : 'text-sm text-gray-500'}
        >
          No fillable fields in this template.
        </p>
      </div>
    );
  }

  return (
    <div className={containerClassName}>
      <div className={inline ? 'space-y-2' : 'space-y-4'}>
        {filteredFields.map((field) => {
          // Override key is context_key or field_reference, NOT field.id
          const overrideKey = getOverrideKey(field);

          // Get current value info (using pending overrides for display)
          const valueInfo = getFieldValueInfo(field, matterContext, overrides);
          // Get saved value info (to show what's persisted)
          const savedValueInfo = savedOverrides
            ? getFieldValueInfo(field, matterContext, savedOverrides)
            : valueInfo;

          const label = getFieldDisplayName(field);
          const required = isFieldRequired(field);
          // Field is missing if it's in the missingFieldIds set (from backend validation)
          const isMissing = missingFieldIds.has(field.id);
          // Also check local validation for immediate feedback
          const localMissing = required && isFieldValueMissing(valueInfo.value);
          const missing = isMissing || localMissing;

          // Check if the field has pending unsaved changes (compare by override key)
          const hasPendingChange =
            savedOverrides !== undefined &&
            overrideKey !== null &&
            overrides[overrideKey] !== savedOverrides[overrideKey];

          // Input styling based on state
          const inputClasses = cn(
            'border rounded focus:outline-none focus:ring-2 w-full',
            inline ? 'px-2 py-1 text-xs' : 'px-3 py-1.5 text-sm',
            missing && 'border-red-300 focus:ring-red-500 bg-red-50',
            !missing &&
              savedValueInfo.source === 'override' &&
              'border-violet-300 bg-violet-50/30',
            !missing &&
              savedValueInfo.source !== 'override' &&
              'border-gray-300 focus:ring-blue-500',
            hasPendingChange && 'ring-2 ring-amber-200',
          );

          return (
            <div key={field.id} className="flex flex-col">
              <label
                htmlFor={field.id}
                className={cn(
                  'font-medium mb-1 flex items-center flex-wrap',
                  inline ? 'text-[11px]' : 'text-sm',
                  missing ? 'text-red-700' : 'text-gray-700',
                )}
              >
                <span>{label}</span>
                {required && <span className="text-red-500 ml-1">*</span>}
                <FieldSourceBadge
                  source={savedValueInfo.source}
                  isMissing={isMissing}
                  inline={inline}
                />
                {hasPendingChange && (
                  <span
                    className={cn(
                      'bg-amber-100 text-amber-700 rounded',
                      inline
                        ? 'text-[9px] ml-1.5 px-1 py-0.5'
                        : 'text-xs ml-2 px-1.5 py-0.5',
                    )}
                  >
                    unsaved
                  </span>
                )}
              </label>
              <input
                id={field.id}
                type="text"
                value={valueInfo.value}
                onChange={handleInputChange(overrideKey ?? field.id)}
                placeholder="Enter value"
                className={inputClasses}
              />
              {missing && (
                <p
                  className={cn(
                    'text-red-600 mt-0.5',
                    inline ? 'text-[9px]' : 'text-xs',
                  )}
                >
                  This field is required
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
